import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useYouTubePlayer, YT_STATE } from "@/hooks/useYouTubePlayer";
import Subtitles from "@/components/player/Subtitles";
import WordCardPanel from "@/components/player/WordCardPanel";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Eye, EyeOff, Keyboard, AlertTriangle, Play, Pause, RotateCcw, RotateCw, Sparkles } from "lucide-react";

function pluralizeWords(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "слово";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "слова";
  return "слов";
}

export default function Player() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startAt = parseFloat(searchParams.get("t")) || 0;
  const { user } = useAuth();
  const [wordClick, setWordClick] = useState(null);
  const [lang, setLang] = useState(null);
  const [hideSubtitles, setHideSubtitles] = useState(false);
  const [skipSeconds, setSkipSeconds] = useState(5);
  const [sessionWordsAdded, setSessionWordsAdded] = useState(0);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  const lastSentRef = useRef(0);
  const seekedFromQueryRef = useRef(false);
  const videoAreaRef = useRef(null);

  const { data: video, refetch: refetchVideo, isError: videoError } = useQuery({
    queryKey: ["video", videoId],
    queryFn: async () => (await api.get(`/videos/${videoId}`)).data,
    retry: false,
  });

  const { data: transcriptData, isLoading: transcriptLoading, error: transcriptError, refetch: refetchTranscript } = useQuery({
    queryKey: ["transcript", videoId, lang],
    queryFn: async () => (await api.get(`/videos/${videoId}/transcript`, { params: lang ? { lang } : {} })).data,
    enabled: !!video,
    retry: false,
  });

  const { playerRef, ready, currentTime, duration, playerState, seekTo, playVideo, pauseVideo } = useYouTubePlayer(
    "youtube-player-container",
    video?.youtube_id,
    startAt || video?.last_position || 0,
    false,
    videoSize
  );

  useEffect(() => {
    if (video && video.status === "not_started") {
      api.patch(`/videos/${videoId}`, { status: "learning" });
    }
  }, [video, videoId]);

  useEffect(() => {
    if (ready && startAt && !seekedFromQueryRef.current) {
      seekedFromQueryRef.current = true;
      seekTo(startAt);
    }
  }, [ready, startAt, seekTo]);

  // Keep the video letterboxed to exactly fit its available area (no black bars)
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;
    const compute = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      const targetRatio = 16 / 9;
      let w = width;
      let h = w / targetRatio;
      if (h > height) {
        h = height;
        w = h * targetRatio;
      }
      setVideoSize({ width: w, height: h });
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!ready || !currentTime) return;
    if (currentTime - lastSentRef.current >= 10) {
      const delta = currentTime - lastSentRef.current;
      const progress = duration ? Math.min(100, (currentTime / duration) * 100) : 0;
      const patch = {
        last_position: currentTime,
        progress_percent: progress,
        watched_seconds_delta: delta,
      };
      const shouldComplete = progress >= 90 && video?.status !== "completed";
      if (shouldComplete) patch.status = "completed";
      api.patch(`/videos/${videoId}`, patch).then(() => {
        if (shouldComplete) refetchVideo();
      });
      lastSentRef.current = currentTime;
    }
  }, [currentTime, ready, duration, videoId, video?.status]);

  // Spacebar toggles play/pause; while subtitles are hidden this reveals/hides the pause overlay
  useEffect(() => {
    const handler = (e) => {
      if (e.code !== "Space") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      if (playerState === YT_STATE.PLAYING) {
        pauseVideo();
      } else {
        playVideo();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [playerState, playVideo, pauseVideo]);

  const handleWordClick = (word, sentence, timestamp) => {
    pauseVideo();
    setWordClick({ word, sentence, timestamp });
    window.getSelection()?.removeAllRanges();
  };

  const handleWordCardClose = () => {
    setWordClick(null);
    if (user?.settings?.autoplay) playVideo();
  };

  const handleWordAdded = () => {
    refetchVideo();
    setSessionWordsAdded((n) => n + 1);
  };

  const handleSeek = (seconds) => {
    seekTo(seconds);
    playVideo();
  };

  const skipBack = () => seekTo(Math.max(0, currentTime - skipSeconds));
  const skipForward = () => seekTo(Math.min(duration || currentTime + skipSeconds, currentTime + skipSeconds));

  if (videoError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-4" data-testid="video-not-found">
        <p className="font-medium">Видео не найдено</p>
        <p className="text-sm text-muted-foreground">Возможно, оно было удалено из библиотеки</p>
        <Button onClick={() => navigate("/library")} data-testid="video-not-found-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Вернуться в библиотеку
        </Button>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const subtitlesPaused = playerState === YT_STATE.PAUSED || playerState === YT_STATE.ENDED;

  return (
    <div className="h-full flex flex-col" data-testid="player-page">
      <div className="flex items-center justify-between px-4 lg:px-6 py-3 border-b border-border shrink-0 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/library")} data-testid="player-back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-heading font-semibold truncate" data-testid="player-video-title">{video.title}</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {sessionWordsAdded > 0 && (
            <span
              className="flex items-center gap-1.5 text-sm font-medium bg-primary/10 text-primary rounded-full px-3 py-1"
              data-testid="session-words-counter"
            >
              <Sparkles className="w-3.5 h-3.5" /> {sessionWordsAdded} {pluralizeWords(sessionWordsAdded)} добавлено
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHideSubtitles((h) => !h)}
            data-testid="toggle-hide-subtitles-button"
          >
            {hideSubtitles ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {hideSubtitles ? "Субтитры скрыты" : "Скрыть субтитры"}
          </Button>
          {video.available_subtitle_langs?.length > 1 && (
            <Select value={lang || video.available_subtitle_langs[0]?.code} onValueChange={setLang}>
              <SelectTrigger className="w-40" data-testid="subtitle-lang-select">
                <SelectValue placeholder="Язык субтитров" />
              </SelectTrigger>
              <SelectContent>
                {video.available_subtitle_langs.map((l) => (
                  <SelectItem key={l.code} value={l.code} data-testid={`subtitle-lang-option-${l.code}`}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div className="lg:w-[65%] shrink-0 bg-black flex flex-col">
          <div ref={videoAreaRef} className="flex-1 flex items-center justify-center overflow-hidden">
            <div style={{ width: videoSize.width || "100%", height: videoSize.height || "100%" }}>
              <div id="youtube-player-container" className="w-full h-full" data-testid="youtube-player" />
            </div>
          </div>
          <div className="shrink-0 bg-black border-t border-white/10 px-3 py-2 flex items-center justify-center gap-2" data-testid="player-custom-controls">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-white hover:bg-white/10"
              onClick={skipBack}
              data-testid="player-skip-back-button"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-white hover:bg-white/10"
              onClick={() => (playerState === YT_STATE.PLAYING ? pauseVideo() : playVideo())}
              data-testid="player-play-pause-button"
            >
              {playerState === YT_STATE.PLAYING ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-white hover:bg-white/10"
              onClick={skipForward}
              data-testid="player-skip-forward-button"
            >
              <RotateCw className="w-4 h-4" />
            </Button>
            <Select value={String(skipSeconds)} onValueChange={(v) => setSkipSeconds(Number(v))}>
              <SelectTrigger className="w-24 h-8 bg-white/10 border-white/20 text-white text-xs" data-testid="player-skip-seconds-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 3, 5, 10].map((s) => (
                  <SelectItem key={s} value={String(s)} data-testid={`player-skip-seconds-${s}`}>{s} сек</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 border-t lg:border-t-0 lg:border-l border-border overflow-hidden relative">
          {transcriptLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : transcriptError ? (
            <div
              className="flex flex-col items-center justify-center h-full text-center p-8 gap-3"
              data-testid="transcript-error-message"
            >
              <AlertTriangle className="w-8 h-8 text-orange-500" />
              <p className="font-medium">Не удалось загрузить субтитры</p>
              <p className="text-sm text-muted-foreground max-w-xs">
                {transcriptError.response?.data?.detail || "Попробуйте открыть видео позже."}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetchTranscript()} data-testid="transcript-retry-button">
                Попробовать снова
              </Button>
            </div>
          ) : (
            <>
              {/* Subtitles stay mounted and keep tracking/auto-scrolling in the background even
                  while visually hidden, so revealing them on pause is instant with no scroll/load jump. */}
              <Subtitles
                transcript={transcriptData?.transcript || []}
                currentTime={currentTime}
                onWordClick={handleWordClick}
                onSeek={handleSeek}
              />
              {hideSubtitles && !subtitlesPaused && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center text-center text-muted-foreground p-8 gap-3 bg-background/97 backdrop-blur-md"
                  data-testid="subtitles-hidden-placeholder"
                >
                  <Keyboard className="w-8 h-8" />
                  <p className="font-medium text-foreground">Субтитры скрыты — слушайте аудио</p>
                  <p className="text-sm max-w-xs">
                    Нажмите <kbd className="bg-muted border border-border rounded px-1.5 py-0.5 text-xs">Пробел</kbd> или
                    поставьте видео на паузу, чтобы увидеть текущую строку и добавить слово
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <WordCardPanel
        wordClick={wordClick}
        video={video}
        onClose={handleWordCardClose}
        onAdded={handleWordAdded}
      />
    </div>
  );
}

